/* ── Saving bytes to disk ──────────────────────────────────────────────────
 *
 * The FIRST save-to-disk path in this app, and deliberately the only one.
 * Before the invoice tracker's export there was no `Blob` handling anywhere in
 * `src/` — no object URL, no anchor click — so this file is where that rule
 * gets written down rather than re-derived per caller.
 *
 * It pairs with `requestBinary` in `@/lib/api`, which is the only place bytes
 * arrive from the server. That function's job ends at `{ filename, blob }`;
 * this one's begins there. Keeping them apart is what lets a caller await the
 * download, decide whether it wants the bytes at all, and report a refusal —
 * `requestBinary` still throws the ordinary `ApiError`, so a 409 or a 429 is
 * handled by the caller exactly as it would be on any JSON endpoint, and
 * nothing has been written to disk by then.
 * ------------------------------------------------------------------------ */

/**
 * The name used when a caller's own name sanitises away to nothing.
 *
 * Extension-less on purpose: this is reached only when there was nothing usable
 * to keep, and inventing `.csv` here would put a claim about the content type
 * into a file whose bytes this function has never looked at.
 */
const FALLBACK_NAME = 'download';

/**
 * How long the object URL is left alive after the click.
 *
 * ⚠️ **Revoking synchronously after `click()` is the tempting version and it is
 * not safe.** Activating a `download` anchor QUEUES the download; the fetch of
 * the blob URL can therefore run after the click call has returned, and a URL
 * revoked in between yields a truncated or empty file — with no error, on some
 * browsers and not others. A macrotask is the smallest delay that puts the
 * revoke after the queued task.
 *
 * ⚠️ It must still be revoked. An un-revoked object URL pins the WHOLE blob in
 * memory for the life of the tab, and an export is the one thing here measured
 * in megabytes — somebody exporting a filtered tracker a dozen times over an
 * afternoon would be holding every one of those copies.
 *
 * If the tab closes before this fires, nothing leaks: the URL dies with the
 * document that created it.
 */
const REVOKE_DELAY_MS = 0;

/**
 * A file name that is safe to hand to a `download` attribute.
 *
 * ## The rule about separators is the one `filenameFromDisposition` applies
 *
 * That function (in `@/lib/api`) reads the server's `Content-Disposition` and
 * **rejects** any name containing a forward or back slash, falling back to the
 * name the caller already knows is sensible. It can reject because it has
 * somewhere to fall back to.
 *
 * This one has nowhere: by the time bytes are in hand, refusing to write them
 * loses the download over a cosmetic problem with its label. So the same
 * characters are treated as unacceptable and the RESPONSE differs — they are
 * **replaced**, not rejected, and the name still says as much as it can.
 *
 * ⚠️ Deliberately NOT "take the last path segment". A basename is also safe,
 * and it silently reinterprets the caller: a name written with a year in front
 * of a slash would be saved having lost the year, with nothing on screen to say
 * so. Replacing keeps every part the caller meant to include.
 *
 * Three more classes go with them, each for its own reason:
 *
 * - **control characters**, which are invisible in a name and can carry a line
 *   break into a header or a log line;
 * - **the Windows-reserved set**, which makes a name unwritable on the platform
 *   this team actually uses, where the browser then mangles it silently;
 * - **leading dots**, so a name cannot start as a parent-directory reference
 *   (which reads as traversal to anything downstream) or become a hidden file.
 *
 * Trailing dots and spaces are trimmed for the same Windows reason.
 */
function safeFileName(name: string): string {
  const cleaned = name
    // Path separators first — the class this function primarily exists for.
    .replace(/[/\\]/g, '-')
    /*
     * Control characters, including the newline that would split a header.
     *
     * `no-control-regex` fires here and the match is the POINT: this class is
     * the thing being stripped, so there is no rewrite that satisfies the rule
     * and still does the job. Written with Unicode escapes rather than literal
     * characters, which is the half of the rule's advice that does apply -- a
     * literal control character in source is invisible in a diff and in review.
     */
    // oxlint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, '')
    // Reserved on Windows; a browser writing one of these renames unpredictably.
    .replace(/[<>:"|?*]/g, '-')
    // A name may not begin with a dot: two of them read as traversal, one hides it.
    .replace(/^\.+/, '')
    // Windows strips these itself, so trimming here keeps the name predictable.
    .replace(/[. ]+$/, '')
    .trim();

  return cleaned === '' ? FALLBACK_NAME : cleaned;
}

/**
 * Save `blob` to the user's disk as `filename`.
 *
 * ```ts
 * const { filename, blob } = await api.exportInvoiceTracker(filters);
 * saveBlob(filename, blob);
 * ```
 *
 * ⚠️ **Do not route this through `Button`.** `ButtonLinkProps` declares
 * `download?: never` on purpose (`src/components/ui/Button.tsx`), because an
 * anchor pointed at a blob URL is not a link a person can middle-click, copy or
 * open in a new tab — it is a one-shot side effect wearing a link's clothes,
 * and the four `never`s exist to say so at the type level. The anchor here is
 * created, clicked and removed within one call and is never part of the
 * rendered tree; nothing can focus it, tab to it, or find it in the
 * accessibility tree. The visible control stays an ordinary `Button` with an
 * `onClick` that awaits the bytes and then calls this.
 *
 * ⚠️ **The name is sanitised, not validated.** See {@link safeFileName} — a
 * separator is replaced rather than refused, because the bytes are already in
 * hand and losing them over the label would be the worse failure. A caller that
 * needs to know whether the server's own name survived intact can compare what
 * it passed with what it received.
 *
 * **The blob is the caller's until this is called and nobody's afterwards.**
 * This function creates the only object URL over it and revokes that URL
 * itself; a caller must not create a second one from the same blob and leave it
 * alive.
 *
 * Returns nothing, and **cannot report success**: the browser owns what happens
 * after the click, and a download blocked by a popup or extension setting looks
 * identical from here. Anything a caller wants to say about the outcome has to
 * be said about the REQUEST — which is where the failures that can be detected
 * actually live.
 */
export function saveBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');

  anchor.href = url;
  anchor.download = safeFileName(filename);
  /*
   * Appended because Firefox does not dispatch a click on a detached anchor,
   * and removed in the `finally` so a throwing click cannot leave a node
   * behind. It is never visible: it carries no text and is gone within the same
   * synchronous block.
   */
  anchor.style.display = 'none';
  document.body.append(anchor);

  try {
    anchor.click();
  } finally {
    anchor.remove();
    // See REVOKE_DELAY_MS: after the queued download task, never before it.
    window.setTimeout(() => URL.revokeObjectURL(url), REVOKE_DELAY_MS);
  }
}
